import QueryString from "qs";
import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

export const useFilterParams = <T extends Record<string, any> = Record<string, any>>(
  initialValue?: Record<string, any>,
) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const previousFiltersRef = useRef<Partial<T>>({});

  const isValidValue = (value: any): boolean => {
    if (value === null || value === undefined) return false
    if (typeof value === 'string' && value.trim() === '') return false
    if (typeof value === 'object' && value.hasOwnProperty('value')) {
      return value.value !== null && value.value !== undefined && value.value !== ''
    }
    return true
  };

  const hasFiltersChanged = (newParams: Partial<T>) => {
    const currentFiltersWithoutPage = { ...newParams } as Record<string, any>;
    delete currentFiltersWithoutPage.page;

    const previousFiltersWithoutPage = { ...previousFiltersRef.current } as Record<string, any>;
    delete previousFiltersWithoutPage.page;

    return JSON.stringify(currentFiltersWithoutPage) !== JSON.stringify(previousFiltersWithoutPage);
  };

  const setFilterParams = (mutationFn: (params: Partial<T>) => Partial<T>) => {
    const parsed = QueryString.parse(searchParams.toString()) as Partial<T>;
    const newParams = Object.fromEntries(
      Object.entries(mutationFn(parsed)).filter(([_, value]) => isValidValue(value)),
    ) as Record<string, any>;

    const filtersChanged = hasFiltersChanged(newParams as Partial<T>);

    if (filtersChanged && newParams.page && Number(newParams.page) > 1) {
      newParams.page = undefined;
    }

    previousFiltersRef.current = { ...newParams } as Partial<T>;

    setSearchParams(QueryString.stringify(newParams));
  };

  const filterParams = QueryString.parse(searchParams.toString()) as T;

  useEffect(() => {
    if (initialValue && searchParams.toString() === '') {
      setSearchParams(
        QueryString.stringify(initialValue)
      );
    }
  }, []);

  return { filterParams, setFilterParams, queryString: searchParams.toString() };
};
